import React, { useState } from 'react';
import { Stack, Text, IconButton, Image } from '@fluentui/react';
import { UrlRecommendationContent } from '../../type';
import IvicosStrings from 'kits/language/stringKit';
import { renderFavicon } from '../../URL/URL.utils';

interface URLWidgetProps {
    content: UrlRecommendationContent;
    setShowImageModal: (show: boolean) => void;
}

const URLWidget: React.FC<URLWidgetProps> = ({ content, setShowImageModal }) => {
    const { links } = content;
    const [scrollIndex, setScrollIndex] = useState(0);
    const [failedFavicons, setFailedFavicons] = useState<Set<string>>(new Set());
    const windowSize = 2;

    const handleFaviconError = (faviconUrl: string) => {
        setFailedFavicons((prev) => new Set(prev).add(faviconUrl));
    };

    if (!links || links.length === 0) {
        return (
            <Stack horizontalAlign="center" verticalAlign="center" style={{ height: '100%' }}>
                <Text variant="small" styles={{ root: { color: '#666' } }}>
                    {IvicosStrings.personalMeetingCornerStrings.url.noLinksAdded}
                </Text>
            </Stack>
        );
    }

    // Clamp scrollIndex
    const maxScroll = Math.max(0, links.length - windowSize);
    const canScrollUp = scrollIndex > 0;
    const canScrollDown = scrollIndex < maxScroll;

    // For smooth scrolling, render all links in a fixed-height container and translateY
    const itemHeight = 56; // px, adjust to match actual row height
    const containerHeight = windowSize * itemHeight;

    return (
        <Stack
            styles={{
                root: {
                    background: '#fff',
                    borderRadius: 10,
                    boxShadow: '0 2px 12px 0 rgba(16,30,54,0.06)',
                    padding: 20,
                    width: 250,
                    height: 240,
                    overflow: 'hidden'
                }
            }}
            onClick={() => setShowImageModal(true)}
        >
            {/* Header with arrows if needed */}
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }} style={{ margin: '10px 0' }}>
                <Text variant="mediumPlus" styles={{ root: { fontWeight: 600, color: '#222' } }}>
                    {links.length > 1
                        ? IvicosStrings.personalMeetingCornerStrings.url.recommendedLinks
                        : IvicosStrings.personalMeetingCornerStrings.url.recommendedLink}
                </Text>
                {links.length > windowSize && (
                    <>
                        <IconButton
                            iconProps={{ iconName: 'ChevronUp' }}
                            disabled={!canScrollUp}
                            onClick={(e) => {
                                e.stopPropagation();
                                setScrollIndex((i) => Math.max(0, i - 1));
                            }}
                            styles={{
                                root: {
                                    marginLeft: 4,
                                    background: 'none',
                                    color: canScrollUp ? '#7356fa' : '#eee',
                                    fontSize: 12,
                                    width: 18,
                                    height: 18,
                                    minWidth: 18,
                                    padding: 0
                                },
                                icon: {
                                    fontSize: 12
                                }
                            }}
                        />
                        <IconButton
                            iconProps={{ iconName: 'ChevronDown' }}
                            disabled={!canScrollDown}
                            onClick={(e) => {
                                e.stopPropagation();
                                setScrollIndex((i) => Math.min(maxScroll, i + 1));
                            }}
                            styles={{
                                root: {
                                    background: 'none',
                                    color: canScrollDown ? '#7356fa' : '#eee',
                                    fontSize: 12,
                                    width: 18,
                                    height: 18,
                                    minWidth: 18,
                                    padding: 0
                                },
                                icon: {
                                    fontSize: 12
                                }
                            }}
                        />
                    </>
                )}
            </Stack>
            {/* Links List with smooth scroll */}
            <div
                style={{
                    height: containerHeight,
                    overflow: 'hidden',
                    position: 'relative',
                    width: '100%'
                }}
            >
                <div
                    style={{
                        transition: 'transform 0.3s cubic-bezier(.4,1.2,.4,1)',
                        transform: `translateY(-${scrollIndex * itemHeight}px)`
                    }}
                >
                    {links.map((link, idx) => (
                        <Stack
                            key={link.id}
                            horizontal
                            verticalAlign="center"
                            tokens={{ childrenGap: 10 }}
                            style={{
                                borderRadius: 8,
                                background: '#fff',
                                padding: '10px 12px',
                                cursor: 'pointer',
                                transition: 'background 0.15s',
                                position: 'relative',
                                boxShadow: 'none',
                                height: itemHeight,
                                boxSizing: 'border-box'
                            }}
                            onClick={() => window.open(link.url, '_blank')}
                            onMouseOver={(e) => (e.currentTarget.style.background = '#edefff')}
                            onMouseOut={(e) => (e.currentTarget.style.background = '#fff')}
                        >
                            {/* favicon in colored circle */}
                            {renderFavicon({
                                favicon: link.favicon,
                                failedFavicons,
                                onFaviconError: handleFaviconError,
                                size: 'medium'
                            })}
                            {/* Link info */}
                            <Stack style={{ flex: 1, minWidth: 0 }}>
                                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
                                    <Text
                                        variant="medium"
                                        styles={{
                                            root: {
                                                fontWeight: 600,
                                                color: '#222',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                maxWidth: 120
                                            }
                                        }}
                                    >
                                        {link.title}
                                    </Text>
                                </Stack>
                                <Text
                                    variant="small"
                                    styles={{
                                        root: {
                                            color: '#666',
                                            fontWeight: 400,
                                            marginTop: 2,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            maxWidth: 180
                                        }
                                    }}
                                >
                                    {link.description || link.siteName || new URL(link.url).hostname}
                                </Text>
                            </Stack>
                        </Stack>
                    ))}
                </div>
            </div>
            {links.length > 0 && (
                <Stack horizontal horizontalAlign="center" verticalAlign="center" tokens={{ childrenGap: 8 }} style={{ margin: '10px 0', marginTop: 'auto' }}>
                    <Text variant="medium" styles={{ root: { color: '#7356fa', fontWeight: 600, cursor: 'pointer' } }} onClick={() => setShowImageModal(true)}>
                        {links.length <= 2
                            ? `${links.length} ${IvicosStrings.personalMeetingCornerStrings.url.links}${links.length === 1 ? '' : 's'}`
                            : `+${links.length - 2} ${IvicosStrings.personalMeetingCornerStrings.url.more}`}
                    </Text>
                </Stack>
            )}
        </Stack>
    );
};

export default URLWidget;
